import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, withKeys as _withKeys, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3371ee70"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "settings-wrapper column inline col-grow q-mx-auto" }
const _hoisted_2 = { class: "settings-page row wide-scroller" }
const _hoisted_3 = { class: "settings-content-wrapper" }
const _hoisted_4 = { class: "settings-left column" }
const _hoisted_5 = { class: "settings-nav shadowboxing" }
const _hoisted_6 = { class: "settings-content shadowboxing" }
const _hoisted_7 = {
  key: 1,
  class: "save-btn-container"
}
const _hoisted_8 = { class: "settings-right column" }

import { useDialogPresets } from '@/services/dialogPresets.composable';
import { useNotify } from '@/services/notify/notify';
import { reactive, computed, onMounted, inject, ref, provide } from 'vue';
import { useLicenseFeatures, useValidPorts, useAfterSettingsUpdate, useSaveSettings, useLinuxInputsValidations } from '@/views/settings/settings.helpers';
import { mapGetters } from '@/store/utils';
import { useRoute, useRouter, onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useStore } from 'vuex';
import QForm from 'quasar/src/components/form/QForm.js';;
import { CONST_NOTIFY } from '@/services/notify/CONST';
import { useI18n } from 'vue-i18n';
import objectPath from 'object-path';
import _ from 'lodash';
import { SetInputsDirty, PortsKeysType, ValidPortsType, InputsDirty, LinuxValidationsKeysType, LinuxValidationsType } from '@/views/settings/settings.types';
import { IKOpenDialog, IKSettingsDirty, IKSettingsLinuxValidInputs, IKSettingsValidPorts, IKSettingsLinuxValidatePorts } from '@/types/injectionKeys';
import { useSettingsPageWindowsQuery, useSettingsPageLinuxQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import elementsIds from '@/const/elementIds.json';
import { useMatomo } from '@/services/matomo/matomo.service';
import { MatomoCategories, MatomoSettingsActions } from '@/services/matomo/matomo.const';
import SettingsSummary from '@/views/settings/components/summary/settings-summary.vue';
import { useOsService } from '@/services/OsService/OsService';
import SettingsTabs from './components/SettingsTabs/SettingsTabs.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'settings',
  setup(__props) {

const matomo = useMatomo();
const coordinatorsService = inject(IKCoordinatorsService);
const osService = useOsService();
const settingsCaller = osService?.isWindows?.value ? useSettingsPageWindowsQuery : useSettingsPageLinuxQuery;
const GQL_Settings = settingsCaller(
  () => ({
    coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
  }),
  { pollInterval: 3000 },
);

const openDialog = inject(IKOpenDialog);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { toastMessage } = useNotify();
const { dialogPresets, closeDialog } = useDialogPresets();
const settingsFormRef = ref<QForm>();
const { t } = useI18n();
const { 'settings/getRexExpValidation': regExpValidation, 'settings/getInputsWithMenuSelectedOption': inputsWithMenuSelectedOption } = mapGetters();
const { validPorts, resetValidationFor, validatePort, PORT_KEYS } = useValidPorts();
const { validLinuxInputs, validateLinuxInput, LINUX_VALIDATION_KEYS, resetLinuxValidation, validateLinuxPorts } = useLinuxInputsValidations();

const { isLicenseContainAPIAccessFeature } = useLicenseFeatures();
const { handleAfterSettingsUpdate, handleAfterIbManagerPortUpdate, handleAfterCoordinatorServerPortUpdate } = useAfterSettingsUpdate();
const { updateSettings, updateIbManagerPort, updateCoordinatorServerPort } = useSaveSettings();

const inputsDirty = ref<InputsDirty>({});
const inputsDirtyDebounceValue = ref(0);

const data = reactive({
  okCancelDialogOpen: {
    pendingRoute: null as any,
  },
  activeConfig: 'General',
  saveBtnLoading: false,
});

const isSaveButtonDisabled = computed(() => {
  const isFetchingOrNotValidPortValidation = Object.values(PORT_KEYS).some(
    portPath => inputsDirty.value[portPath] && String(inputsDirty.value[portPath]) === String(validPorts.value[portPath].port) && !validPorts.value[portPath].isValid,
  );
  if (osService?.isWindows?.value) {
    return !Object.keys(inputsDirty.value).length || isFetchingOrNotValidPortValidation || !regExpValidation.value;
  }
  if (osService?.isUnix?.value) {
    const isLinuxInputsNotValid = Object.values(LINUX_VALIDATION_KEYS).some(linuxInputPath => validLinuxInputs.value[linuxInputPath].isValid === false);
    return !Object.keys(inputsDirty.value).length || isFetchingOrNotValidPortValidation || isLinuxInputsNotValid;
  }
  return true;
});

const GQL_SettingsComputed = computed(() => {
  if (!GQL_Settings.result.value) return null;
  const dirtyObj = Object.keys(inputsDirty.value).reduce((previousValue, key) => {
    objectPath.set(previousValue, key, inputsDirty.value[key as keyof typeof inputsDirty.value]);
    return previousValue;
  }, {});
  return _.merge(_.cloneDeep(GQL_Settings.result.value.ibManagerEntities), dirtyObj);
});
const showSaveDiscardSection = computed(() => route.name === 'settings.Agents' || route.name === 'settings.General');

const discardSettings = () => {
  data.saveBtnLoading = false;
  Object.keys(validPorts.value).forEach(key => resetValidationFor(key as keyof ValidPortsType));
  if (osService?.isUnix?.value) {
    Object.keys(validLinuxInputs.value).forEach(key => resetLinuxValidation(key as keyof LinuxValidationsType));
  }
  Object.keys(inputsDirty.value).forEach(key => delete inputsDirty.value[key as keyof typeof inputsDirty.value]);
};

const handlePortInputs = async (field: PortsKeysType, port: number) => {
  resetValidationFor(field);
  setTimeout(async () => {
    if (inputsDirty.value[field] === port) {
      await validatePort(field, port);
      settingsFormRef.value?.validate();
    }
  }, 1000);
};
const setInputsDirty: SetInputsDirty = (path, value) => {
  // common port for Windows/Linux
  if (path === PORT_KEYS.ibManagerPort) {
    handlePortInputs(path as PortsKeysType, Number(value));
  }
  if (osService?.isWindows.value && Object.values(PORT_KEYS).includes(path as PortsKeysType)) handlePortInputs(path as PortsKeysType, Number(value));
  if (osService?.isUnix.value && Object.values(LINUX_VALIDATION_KEYS).includes(path as LinuxValidationsKeysType)) validateLinuxInput(path as LinuxValidationsKeysType, value);
  if (String(objectPath.get(GQL_Settings.result?.value?.ibManagerEntities || {}, path)) === String(value)) delete inputsDirty.value[path];
  else inputsDirty.value[path] = value;
  inputsDirtyDebounceValue.value += 1;
  const clonedInputsDirtyDebounceValue = inputsDirtyDebounceValue.value;

  setTimeout(() => {
    if (clonedInputsDirtyDebounceValue === inputsDirtyDebounceValue.value) {
      matomo?.trackEvent({
        category: MatomoCategories.Settings,
        action: 'Change Input',
        name: path,
        value: typeof value === 'boolean' || typeof value === 'number' ? Number(value) : undefined,
      });
    }
  }, 1000);
};
const saveSettingsGQL = async () => {
  try {
    const isFormValid = await settingsFormRef.value?.validate();
    if (isFormValid) {
      data.saveBtnLoading = true;
      try {
        const dirtyInputs = Object.keys(inputsDirty.value) as (keyof typeof inputsDirty.value)[];
        dirtyInputs.forEach(key => {
          const value = inputsDirty.value[key];
          matomo?.trackEvent({
            category: MatomoCategories.Settings,
            action: MatomoSettingsActions.Save,
            name: key,
            value: typeof value === 'boolean' || typeof value === 'number' ? Number(value) : undefined,
          });
        });
        const saveSettingsResult = await updateSettings(inputsDirty.value);
        handleAfterSettingsUpdate(saveSettingsResult);
      } catch (error) {
        toastMessage(t('settings.notifications.settingsSavedUnSuccessfully.text'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
      }

      if (inputsDirty.value['settings.common.ibManager.ibManagerPort']) {
        let isFetching = true;
        setTimeout(() => {
          if (isFetching) dialogPresets.value.pleaseHold();
        }, 5000);
        try {
          const saveIbManagerPortResult = await updateIbManagerPort({ 'settings.common.ibManager.ibManagerPort': inputsDirty.value['settings.common.ibManager.ibManagerPort'] });
          isFetching = false;
          closeDialog();
          handleAfterIbManagerPortUpdate(saveIbManagerPortResult);
        } catch (error) {
          toastMessage(t('settings.notifications.settingsSavedUnSuccessfully.text'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
        }
      }

      if (inputsDirty.value['settings.windows.coordinator.restServicePort']) {
        let isFetching = true;
        setTimeout(() => {
          if (isFetching) dialogPresets.value.pleaseHold();
        }, 5000);
        try {
          const saveCoordinatorServerPortResult = await updateCoordinatorServerPort({
            'settings.windows.coordinator.restServicePort': inputsDirty.value['settings.windows.coordinator.restServicePort'],
          });
          handleAfterCoordinatorServerPortUpdate(saveCoordinatorServerPortResult);
        } catch (error) {
          toastMessage(t('settings.notifications.settingsSavedUnSuccessfully.text'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
        }
        isFetching = false;
        closeDialog();
      }

      await GQL_Settings.refetch();
      discardSettings();
    }
  } finally {
    data.saveBtnLoading = false;
  }
};

const showDiscardDialog = (okCallback: { (): void }, cancelCallback: { (): void }) => {
  if (openDialog) {
    openDialog('IbOkCancelDialog', {
      width: 'unset',
      height: 'unset',
      grayBackground: true,
      value: true,
      isOpen: 'false',
      dialogTitle: t('settings.dialogs.okCancelDialog.title'),
      body: t('settings.dialogs.okCancelDialog.body'),
      okLabel: t('settings.dialogs.okCancelDialog.okLabel'),
      cancelLabel: t('settings.dialogs.okCancelDialog.cancelLable'),
      styleOverride: { maxHeight: '224px', minHeight: '224px !important' },
      okCallback,
      cancelCallback,
    });
  }
};

const applyConfigView = async (configView: string) => {
  data.activeConfig = configView;
  await router.push({ name: `settings.${configView}` });
  settingsFormRef.value?.validate();
};

const changeConfigView = async (configView: string = '') => {
  if (!isSaveButtonDisabled.value) {
    showDiscardDialog(
      async () => {
        await saveSettingsGQL();
        store.commit('main/dialogState', false);
        if (configView) await applyConfigView(configView);
      },
      async () => {
        discardSettings();
        store.commit('main/dialogState', false);
        if (configView) await applyConfigView(configView);
      },
    );
  } else if (configView) await applyConfigView(configView);
};
// TODO GRAPHQL - move to presistent data
const handleInputWithMenuSelection = ({ option, componentId }: any) => {
  localStorage.setItem(
    'settings-inputs-with-menu',
    JSON.stringify({
      ...inputsWithMenuSelectedOption.value,
      [componentId]: option,
    }),
  );
  store.commit('settings/setInputWithMenuSelectedOption', { [componentId]: option });
};

onMounted(async () => {
  store.commit('settings/setInputWithMenuSelectedOption', JSON.parse(localStorage.getItem('settings-inputs-with-menu') || '{}') || {});
  const config = route?.name?.toString().split('.')[1];
  if (config) await changeConfigView(config);
});

provide(IKSettingsDirty, { inputsDirty, setInputsDirty });
provide(IKSettingsValidPorts, validPorts);
provide(IKSettingsLinuxValidInputs, validLinuxInputs);
provide(IKSettingsLinuxValidatePorts, validateLinuxPorts);

onBeforeRouteUpdate(() => {
  changeConfigView();
});

onBeforeRouteLeave((to, from, next) => {
  if (!isSaveButtonDisabled.value) {
    showDiscardDialog(
      async () => {
        await saveSettingsGQL();
        data.okCancelDialogOpen.pendingRoute();
        store.commit('main/dialogState', false);
      },
      () => {
        data.okCancelDialogOpen.pendingRoute();
        store.commit('main/dialogState', false);
      },
    );
    data.okCancelDialogOpen.pendingRoute = next;
  } else next();
});

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!
  const _component_q_btn = _resolveComponent("q-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(QForm), {
      ref_key: "settingsFormRef",
      ref: settingsFormRef,
      class: "column inline no-wrap q-px-lg q-pb-lg col-grow",
      onKeydown: _cache[0] || (_cache[0] = _withKeys(_withModifiers(() => null, ["prevent"]), ["enter"]))
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(SettingsTabs, { onChange: changeConfigView })
              ]),
              _createElementVNode("div", _hoisted_6, [
                (_unref(GQL_SettingsComputed))
                  ? (_openBlock(), _createBlock(_component_router_view, {
                      key: 0,
                      ref: "tab-view",
                      "settings-object": _unref(GQL_SettingsComputed),
                      "handle-input-with-menu-selection": handleInputWithMenuSelection,
                      "has-api-access-feature": _unref(isLicenseContainAPIAccessFeature)
                    }, null, 8, ["settings-object", "has-api-access-feature"]))
                  : _createCommentVNode("", true),
                (_unref(showSaveDiscardSection))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("span", null, [
                        _createVNode(_component_q_btn, {
                          unelevated: "",
                          class: "setting-discard-btn ibf-oneline1",
                          label: "Discard All",
                          "data-testid": _unref(elementsIds).settings.discardButton,
                          "no-caps": "",
                          disable: data.saveBtnLoading || !Object.keys(inputsDirty.value).length,
                          onClick: discardSettings
                        }, null, 8, ["data-testid", "disable"]),
                        _createVNode(_component_q_btn, {
                          flat: "",
                          class: _normalizeClass(`${!_unref(isSaveButtonDisabled) ? 'setting-save-btn' : 'setting-save-btn-disabled'} ibf-oneline1`),
                          label: "Save All",
                          type: "submit",
                          loading: data.saveBtnLoading,
                          "no-caps": "",
                          disable: _unref(isSaveButtonDisabled),
                          "data-testid": _unref(elementsIds).settings.saveButton,
                          onClick: _withModifiers(saveSettingsGQL, ["prevent"])
                        }, null, 8, ["class", "loading", "disable", "data-testid", "onClick"])
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(SettingsSummary)
            ])
          ])
        ])
      ]),
      _: 1
    }, 512)
  ]))
}
}

})